/**
 * ISO 3166-1 alpha-2 country codes. Display names are derived at runtime via
 * `Intl.DisplayNames`, so we only need to maintain the code list here.
 */
const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AR', 'AT', 'AU', 'AW', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BM', 'BN', 'BO', 'BR',
  'BS', 'BT', 'BW', 'BY', 'BZ', 'CA', 'CD', 'CF', 'CG', 'CH', 'CI', 'CL', 'CM',
  'CN', 'CO', 'CR', 'CU', 'CV', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FM', 'FR', 'GA', 'GB', 'GD',
  'GE', 'GH', 'GM', 'GN', 'GQ', 'GR', 'GT', 'GW', 'GY', 'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IN', 'IQ', 'IR', 'IS', 'IT', 'JM', 'JO', 'JP', 'KE', 'KG',
  'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK',
  'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MR', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NE',
  'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NZ', 'OM', 'PA', 'PE', 'PG', 'PH', 'PK',
  'PL', 'PT', 'PW', 'PY', 'QA', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD',
  'SE', 'SG', 'SI', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SY',
  'SZ', 'TD', 'TG', 'TH', 'TJ', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TZ',
  'UA', 'UG', 'US', 'UY', 'UZ', 'VC', 'VE', 'VN', 'VU', 'WS', 'YE', 'ZA', 'ZM',
  'ZW',
] as const;

export interface Country {
  /** ISO 3166-1 alpha-2 code, e.g. "US". */
  code: string;
  /** Localized display name, e.g. "United States". */
  name: string;
}

/**
 * Country options sorted alphabetically by localized name. Computed once at
 * module load. Falls back to the raw code if `Intl.DisplayNames` is unavailable.
 */
export const COUNTRIES: Country[] = (() => {
  let displayNames: Intl.DisplayNames | undefined;
  try {
    displayNames = new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    displayNames = undefined;
  }

  return COUNTRY_CODES.map((code) => ({
    code,
    name: displayNames?.of(code) ?? code,
  })).sort((a, b) => a.name.localeCompare(b.name));
})();

const VALID_CODES = new Set<string>(COUNTRY_CODES);

/** Type guard for a supported country code. */
export function isValidCountryCode(code: string): boolean {
  return VALID_CODES.has(code.toUpperCase());
}

/**
 * Best-effort country detection from the browser locale (e.g. "en-US" → "US").
 * Returns an empty string when no supported region can be resolved, so the
 * caller can leave the field unselected rather than guessing wrong.
 */
export function detectCountryFromBrowser(): string {
  if (typeof navigator === 'undefined') return '';

  const languages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const locale of languages) {
    if (!locale) continue;
    try {
      const region = new Intl.Locale(locale).maximize().region;
      if (region && VALID_CODES.has(region)) return region;
    } catch {
      // Fall back to a simple "xx-REGION" parse.
      const region = locale.split('-')[1]?.toUpperCase();
      if (region && VALID_CODES.has(region)) return region;
    }
  }

  return '';
}
